// Сколько подряд рисоваться «в никуда», чтобы признать цикл отрисовки мертвым
export const RENDER_STALL_LIMIT_MS = 3000;

// Промежуток между нашими же тиками, выше которого тормозили НАС, а не Phaser:
// длинная пауза сборщика мусора, тяжелый кадр, свернутая вкладка. Такие тики в
// счет простоя не идут вовсе
export const RENDER_STALL_MAX_TICK_GAP_MS = 250;

// Отсрочка после любой заморозки наших тиков и после запуска сторожа. Пока она
// идет, тревогу не поднимаем.
//
// Нужна из-за возвращения из фона: приложение приходит в себя не мгновенно —
// поле пересобирается по свежему состоянию, а порог перерисовки в GameContent
// может заодно перемонтировать сцену, и новая игра бутится заново. В этом
// промежутке счетчик кадров честно стоит, хотя ничего не сломано. Живой тест
// на смартфоне: сворачивание и разворачивание браузера давало ровно такую
// ложную тревогу через 1-2 секунды после того, как поле уже рисовалось.
//
// Настоящему зависанию отсрочка не мешает: мертвый цикл не оживает никогда,
// так что тревога просто придет на несколько секунд позже
export const RENDER_STALL_GRACE_MS = 5000;

/**
 * Сторож зависшего рендера поля.
 *
 * Зачем: исключение внутри кадра убивает игровой цикл Phaser насовсем — свой
 * requestAnimationFrame он планирует ПОСЛЕ вызова колбэка (см.
 * dom/RequestAnimationFrame.js), и после броска следующий кадр уже не
 * заказывается. Канва застывает на том, что успело нарисоваться, и сколько
 * ни жди, ничего не изменится (см. src/utils/sprites/generateOutlineTexture.js
 * — там разобран случай, из-за которого это ловили пользователи).
 *
 * Как отличается зависание от честной паузы: часами служит НАШ собственный
 * requestAnimationFrame. Когда браузер перестает анимировать страницу
 * (вкладка свернута, экран погас, система душит фон), наши тики замирают
 * вместе с ходом Phaser — простой не копится. Копится он только пока браузер
 * рисует нас в нормальном темпе, а счетчик кадров Phaser стоит на месте:
 * это и есть мертвый цикл, другого объяснения у такой пары нет.
 *
 * Потеря фокуса окном (переключились на другое приложение, окно осталось
 * видимым) ложной тревоги не дает: Phaser на blur только перестает
 * сглаживать дельту (TimeStep.smoothDelta), а frame++ продолжает.
 *
 * Итоговое правило простое: тревога — это graceMs + stallLimitMs НЕПРЕРЫВНО
 * стоящего счетчика кадров, пока браузер рисует нас в обычном темпе, а
 * страница видима. Любой ожившый кадр возвращает отсрочку целиком.
 *
 * getFrame — номер кадра игры (Phaser.Game.getFrame), onStall — что делать с
 * зависшим полем. Возвращает функцию тика: зовите ее из requestAnimationFrame.
 */
export const createRenderStallWatchdog = ({
  getFrame,
  onStall,
  isPageVisible = () => document.visibilityState === 'visible',
  stallLimitMs = RENDER_STALL_LIMIT_MS,
  maxTickGapMs = RENDER_STALL_MAX_TICK_GAP_MS,
  graceMs = RENDER_STALL_GRACE_MS,
}) => {
  let previousFrame = null;
  let previousNow = null;
  let stalledMs = 0;
  // Отсрочка отсчитывается со старта: сцена только создается
  let graceLeftMs = graceMs;

  return (now) => {
    const frame = getFrame();
    const delta = previousNow === null ? 0 : now - previousNow;
    previousNow = now;

    // Кадр сменился — цикл жив. Отсрочка возвращается целиком: она должна
    // прощать КАЖДЫЙ переходный период, а не первый попавшийся. Иначе за
    // партию она понемногу истратилась бы на мелких заминках, и к вечеру
    // сторож стал бы срываться на ровном месте
    if (frame !== previousFrame) {
      previousFrame = frame;
      stalledMs = 0;
      graceLeftMs = graceMs;
      return;
    }

    if (delta <= 0 || delta > maxTickGapMs || !isPageVisible()) {
      // Нас замораживали (фон, тяжелая пауза) — приложению дается время
      // прийти в себя, прежде чем судить его за стоящий счетчик кадров
      stalledMs = 0;
      graceLeftMs = graceMs;
      return;
    }

    if (graceLeftMs > 0) {
      graceLeftMs -= delta;
      stalledMs = 0;
      return;
    }

    stalledMs += delta;

    if (stalledMs >= stallLimitMs) {
      // Обнуляем сразу: перерисовка не мгновенная, и без сброса тревога
      // повторялась бы каждый следующий кадр. Заодно новая отсрочка — пока
      // поле пересобирается, счетчик кадров опять постоит
      stalledMs = 0;
      graceLeftMs = graceMs;
      onStall();
    }
  };
};
